import React, { useState, useContext, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faPaperclip, 
  faTasks,
  faCalendarAlt,
  faSave,
  faChevronRight,
  faInfoCircle,
  faCalendarCheck,
  faBuilding,
  faHotel,
  faRoute,
  faCalendar,
  faCheckCircle,
  faBan,
  faTrash,
  faMapMarkerAlt,
  faSuitcase,
  faClipboardCheck,
  faAddressBook,
  faUser,
  faPhone,
  faCar,
  faEdit,
  faUserTie,
  faTicketAlt,
  faChevronLeft,
  faExclamationTriangle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  doc, 
  updateDoc, 
  getDoc, 
  deleteDoc,
  collection, 
  query, 
  getDocs,
  orderBy, 
  onSnapshot
} from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import TourContext from '../../context/TourContext';
import UserContext from '../../context/UserContext';
import TopBar from '../TopBar';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import AuthCheckWrapper from '../AuthCheckWrapper';
import TicketModal from './TicketModal';
import FileViewerModal from '../common/FileViewerModal';
import styles from './Tickets.module.css';
import defaultProfilePhoto from '../../assets/Images/defaultUserProfilePicture.png';

const Tickets = () => {
  const { eventId, dateId } = useParams();
  const { selectedEvent, selectedDate, setSelectedDate, updateDate } = useContext(TourContext);
  const { userData, authToken } = useContext(UserContext);
  const [tickets, setTickets] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [groupedTicketData, setGroupedTicketData] = useState([]);
  const [isLoadingTicketData, setIsLoadingTicketData] = useState(false);

  // Determine event type
  const eventType = selectedEvent?.type || 'tour';

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle date scroll
  const handleScroll = (direction) => {
    const scrollContainer = document.getElementById('dateScroller');
    if (scrollContainer) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      scrollContainer.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    setSelectedDate(date.id);
  };

  // User data fetching functions
  const fetchUserData = async (userId) => {
    if (!userId) return null;
    
    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (error) {
      console.error(`Error fetching user data for ${userId}:`, error);
      return null;
    }
  };

  // Get user data by ID - updated to check assignedUsers subcollection
  const getTourMemberById = async (userId) => {
    if (!userId || !selectedEvent) return null;
    
    try {
      // First try to get fresh data from Firestore
      const userData = await fetchUserData(userId);
      if (userData) {
        return {
          fullName: userData.fullName || userData.displayName || 'Unknown',
          profilePictureUrl: userData.profilePictureUrl || null,
          userType: userData.userType || 'Unknown'
        };
      }
      
      // Fallback to checking the assignedUsers subcollection
      const db = getFirestore();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      const assignedUserRef = doc(db, collectionName, selectedEvent.id, 'assignedUsers', userId);
      const assignedUserDoc = await getDoc(assignedUserRef);
      
      if (assignedUserDoc.exists()) {
        const assignedUserData = assignedUserDoc.data();
        return {
          fullName: assignedUserData.fullName || assignedUserData.displayName || 'Unknown User',
          profilePictureUrl: assignedUserData.profilePictureUrl || null,
          userType: assignedUserData.userType || 'Unknown'
        };
      }

      return {
        fullName: 'Unknown User',
        profilePictureUrl: null,
        userType: 'Unknown'
      };
    } catch (error) {
      console.error(`Error in getTourMemberById for ${userId}:`, error);
      return {
        fullName: 'Unknown User',
        profilePictureUrl: null,
        userType: 'Unknown'
      };
    }
  };

  // Fetch tickets from tickets subcollection
  useEffect(() => {
    if (!selectedEvent?.id || !selectedDate) {
      setTickets([]);
      return;
    }
    
    const collectionName = eventType === 'tour' ? 'tours' : 'shows';
    const db = getFirestoreInstance();
    
    // Set up listener on tickets subcollection
    const ticketsQuery = query(
      collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'tickets'),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(ticketsQuery, (snapshot) => {
      if (snapshot.empty) {
        setTickets([]);
        return;
      }
      
      const ticketsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setTickets(ticketsList);
    }, (error) => {
      console.error('Error fetching tickets:', error);
      // Fallback to one-time query if listener fails
      getDocs(ticketsQuery).then(snapshot => {
        if (snapshot.empty) {
          setTickets([]);
          return;
        }
        
        const ticketsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setTickets(ticketsList);
      }).catch(queryError => {
        console.error('Error in fallback query:', queryError);
        setTickets([]);
      });
    });
    
    // Clean up listener
    return () => unsubscribe();
  }, [selectedEvent, selectedDate, eventType]);

  // Process tickets data for display
  useEffect(() => {
    const processTickets = async () => {
      if (tickets.length === 0) {
        setGroupedTicketData([]);
        return;
      }
      
      setIsLoadingTicketData(true);
      try {
        const passengerDataCache = {};
        const grouped = {};
        
        for (const ticket of tickets) {
          if (ticket.passengers && Array.isArray(ticket.passengers) && ticket.passengers.length > 0) {
            // Create a key based on passenger IDs (more reliable than names)
            const passengerKey = ticket.passengers.map(p => p.userId).sort().join('_');
            
            if (!grouped[passengerKey]) {
              grouped[passengerKey] = {
                passengers: [],
                tickets: []
              };
              
              // Fetch passenger data for display
              for (const passenger of ticket.passengers) {
                if (!passenger || !passenger.userId) continue;
                
                // Check if we already fetched this passenger's data
                if (!passengerDataCache[passenger.userId]) {
                  const memberData = await getTourMemberById(passenger.userId);
                  if (memberData) {
                    passengerDataCache[passenger.userId] = memberData;
                  } else {
                    // Fallback to data in the ticket
                    passengerDataCache[passenger.userId] = {
                      fullName: passenger.fullName || passenger.name || 'Unknown',
                      profilePictureUrl: passenger.profilePictureUrl || null,
                      userType: passenger.userType || 'Unknown'
                    };
                  }
                }
                
                // Add to the passengers array with the cached data
                grouped[passengerKey].passengers.push({
                  ...passenger,
                  ...passengerDataCache[passenger.userId]
                });
              }
            }
            
            grouped[passengerKey].tickets.push(ticket);
          } else {
            // Handle tickets without passengers
            const noPassengerKey = 'no_passengers_' + ticket.id;
            if (!grouped[noPassengerKey]) {
              grouped[noPassengerKey] = {
                passengers: [{
                  userId: 'unknown',
                  fullName: 'Unassigned Ticket',
                  profilePictureUrl: null,
                  userType: 'Unknown'
                }],
                tickets: []
              };
            }
            grouped[noPassengerKey].tickets.push(ticket);
          }
        }
        
        setGroupedTicketData(Object.values(grouped));
      } catch (error) {
        console.error('Error processing ticket data:', error);
        setGroupedTicketData([]);
      } finally {
        setIsLoadingTicketData(false);
      }
    };
    
    processTickets();
  }, [tickets, selectedEvent]);

  const handleAddTicket = () => {
    setEditingTicket(null);
    setIsModalOpen(true);
  };

  const handleEditTicket = (ticket) => {
    setEditingTicket(ticket);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTicket(null);
  };

  const deleteFile = async (filePath, authToken) => {
    if (!filePath || !authToken) return { success: false, error: 'Missing required parameters' };
    
    try {
      console.log('Deleting file using deleteBackblazeFile API:', filePath);
      
      const response = await fetch('/api/deleteBackblazeFile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ filePath })
      });
      
      // Check the response
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete file');
        } else {
          const errorText = await response.text();
          console.error('Non-JSON error response:', errorText);
          throw new Error('Server returned an unexpected response format');
        }
      }
      
      const responseData = await response.json();
      console.log('Delete successful, response:', responseData);
      
      return { success: true };
    } catch (error) {
      console.error(`Error deleting file '${filePath}':`, error);
      return { success: false, error: error.message };
    }
  };

  const handleDeleteTicket = (ticket) => {
    setDeleteConfirmation({
      isOpen: true,
      ticketId: ticket.id
    });
  };

  // State for delete confirmation
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    ticketId: null
  });

  // Updated to delete from tickets subcollection
  const confirmDelete = async () => {
    try {
      setIsDeleting(true); // Start loading
      const db = getFirestore();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Find the ticket to delete in our local state
      const ticketToDelete = tickets.find(ticket => ticket.id === deleteConfirmation.ticketId);
      
      if (!ticketToDelete) {
        throw new Error('Ticket not found');
      }
      
      // Delete file from storage if it exists
      if (ticketToDelete && ticketToDelete.ticketFilePath) {
        const deleteResult = await deleteFile(ticketToDelete.ticketFilePath, authToken);
        if (!deleteResult.success) {
          console.warn('Failed to delete file from storage:', deleteResult.error);
        }
      }
      
      // Delete the ticket document from the subcollection
      const ticketDocRef = doc(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'tickets', deleteConfirmation.ticketId);
      await deleteDoc(ticketDocRef);
      
      // The listener will update the UI automatically
    } catch (error) {
      console.error('Error deleting ticket:', error);
      alert('Failed to delete ticket. Please try again.');
    } finally {
      setIsDeleting(false); // Stop loading
      setDeleteConfirmation({
        isOpen: false,
        ticketId: null
      });
    }
  };

  // Helper function to calculate duration
  const calculateDuration = (departure, arrival) => {
    if (!departure || !arrival || !departure.seconds || !arrival.seconds) {
      return 'Unknown';
    }
    
    const diff = (arrival.seconds - departure.seconds) * 1000;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  // Get airline icon
  const getAirlineIcon = (airline, travelMode) => {
    const icons = {
      IndiGo: require('../../assets/Images/indigologo.png'),
      'American Airlines': require('../../assets/Images/americanairlineslogo.png'), 
      'Alaska Airlines': require('../../assets/Images/alaskaairlineslogo.png'), 
      'Air Canada': require('../../assets/Images/aircanadalogo.png'),
      'Air China': require('../../assets/Images/airchinalogo.png'),  
      'Air India': require('../../assets/Images/airindialogo.png'), 
      'Air India Express': require('../../assets/Images/airindiaexpresslogo.png'), 
      'Air Astana': require('../../assets/Images/airastanalogo.png'), 
      'Austrian Airlines': require('../../assets/Images/austrianairlineslogo.png'), 
      Vistara: require('../../assets/Images/vistaralogo.png'), 
      'Akasa Air': require('../../assets/Images/akasaairlogo.png'), 
      'Air France': require('../../assets/Images/airfrancelogo.png'), 
      'Cathay Pacific': require('../../assets/Images/cathaypacificlogo.png'), 
      'British Airways': require('../../assets/Images/britishairwayslogo.png'), 
      'Brussels Airlines': require('../../assets/Images/brusselsairlineslogo.png'), 
      'Delta Airlines': require('../../assets/Images/deltaairlineslogo.png'), 
      Emirates: require('../../assets/Images/emirateslogo.png'), 
      Ethithad: require('../../assets/Images/etihadlogo.png'), 
      'Ethiopian Airlines': require('../../assets/Images/ethiopianairlineslogo.png'), 
      Finnair: require('../../assets/Images/finnairlogo.png'),  
      flynas: require('../../assets/Images/flynaslogo.png'), 
      'ITA Airways': require('../../assets/Images/itaairwayslogo.png'), 
      'KLM Airlines': require('../../assets/Images/klmairlineslogo.png'), 
      'Korean Air': require('../../assets/Images/koreanairlogo.png'), 
      Lufthansa: require('../../assets/Images/lufthansalogo.png'), 
      'Qantas': require('../../assets/Images/qantaslogo.png'), 
      'Qatar Airways': require('../../assets/Images/qatarairwayslogo.png'), 
      'Royal Air Maroc': require('../../assets/Images/royalairmaroclogo.png'), 
      'Salam Air': require('../../assets/Images/salamairlogo.png'), 
      'Saudi Arabian': require('../../assets/Images/airindialogo.png'),
      'Singapore Airlines': require('../../assets/Images/singaporeairlineslogo.png'), 
      'SriLankan Airlines': require('../../assets/Images/srilankanairlineslogo.png'), 
      SpiceJet: require('../../assets/Images/spicejetlogo.png'), 
      'Swiss Airlines': require('../../assets/Images/swissairlineslogo.png'), 
      'Turkish Airlines': require('../../assets/Images/turkishairlineslogo.png'), 
      'United Airlines': require('../../assets/Images/unitedairlineslogo.png'), 
      'Virgin Atlantic': require('../../assets/Images/virginatlanticlogo.png'), 
      Flight: require('../../assets/Images/flightlogo.png'),
      Bus: require('../../assets/Images/bus.png'),
      Train: require('../../assets/Images/train.png'),
    };
    
    // Try to find the airline icon first
    if (airline && icons[airline]) {
      return icons[airline];
    }
    
    // Fall back to travel mode icon
    if (travelMode && icons[travelMode]) {
      return icons[travelMode];
    }
    
    // Default to flight icon as a last resort
    return icons.Flight;
  };

  // VisibilityUsers component - Updated for assignedUsers subcollection
  const VisibilityUsers = ({ visibilityIds }) => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showAll, setShowAll] = useState(false);
    
    useEffect(() => {
      const fetchVisibilityUsers = async () => {
        if (!Array.isArray(visibilityIds) || visibilityIds.length === 0) {
          setUsers([]);
          setIsLoading(false);
          return;
        }
        
        setIsLoading(true);
        const userDataList = [];
        
        for (const userId of visibilityIds) {
          const userData = await getTourMemberById(userId);
          if (userData) {
            userDataList.push({
              userId,
              ...userData
            });
          }
        }
        
        setUsers(userDataList);
        setIsLoading(false);
      };
      
      fetchVisibilityUsers();
    }, [visibilityIds]);
    
    if (isLoading) {
      return <span className={styles['visibility-loading']}>Loading...</span>;
    }
    
    if (users.length === 0) {
      return <span className={styles['visibility-all']}>All event members</span>;
    }
    
    const displayUsers = showAll ? users : users.slice(0, 2);
    const remainingCount = users.length - 2;
    
    return (
      <div className={styles['visibility-users']}>
        {displayUsers.map(user => (
          <div key={user.userId} className={styles['visibility-user']}>
            <img 
              src={user.profilePictureUrl || defaultProfilePhoto}
              alt={user.fullName}
              className={styles['visibility-user-pic']}
            />
            <span>{user.fullName}</span>
          </div>
        ))}
        
        {!showAll && remainingCount > 0 && (
          <button
            className={styles['show-more-button']}
            onClick={(e) => {
              e.stopPropagation();
              setShowAll(true);
            }}
          >
            +{remainingCount} more
          </button>
        )}
        
        {showAll && users.length > 2 && (
          <button
            className={styles['show-less-button']}
            onClick={(e) => {
              e.stopPropagation();
              setShowAll(false);
            }}
          >
            Show less
          </button>
        )}
      </div>
    );
  };

  // Render ticket cards
  const renderTicketCards = () => {
    if (tickets.length === 0) {
      return (
        <div className={styles['empty-state']}>
          <FontAwesomeIcon icon={faTicketAlt} className={styles['empty-icon']} />
          <p className={styles['empty-text']}>No tickets for this date</p>
        </div>
      );
    }
    
    if (isLoadingTicketData) {
      return (
        <div className={styles['loading-state']}>
          <FontAwesomeIcon icon={faSpinner} spin />
          <p>Loading ticket data...</p>
        </div>
      );
    }
    
    return groupedTicketData.map((group, index) => (
      <div key={index} className={styles['passenger-section']}>
        <div className={styles['passenger-name']}>
          <div className={styles['passenger-list']}>
            {group.passengers.map((passenger, idx) => (
              <div key={`${passenger.userId}_${idx}`} className={styles['passenger-item']}>
                <img 
                  src={passenger.profilePictureUrl || defaultProfilePhoto}
                  alt={passenger.fullName}
                  className={styles['passenger-avatar']}
                />
                <span>{passenger.fullName}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={styles['passenger-tickets']}>
          {group.tickets.map((ticket) => (
            <div key={ticket.id} className={styles['ticket-card']}>
              <div className={styles['ticket-header']}>
                <div className={styles['airline-info']}>
                  <img 
                    src={getAirlineIcon(ticket.airlines, ticket.travelMode)} 
                    alt={ticket.airlines || ticket.travelMode}
                    className={styles['airline-logo']}
                  />
                  <div>
                    <span className={styles['airline-name']}>{ticket.airlines || ticket.travelMode}</span>
                    {(ticket.flightNumber || ticket.trainNumber || ticket.busNumber) && (
                      <span className={styles['ticket-number']}>
                        {ticket.flightNumber || ticket.trainNumber || ticket.busNumber}
                      </span>
                    )}
                  </div>
                </div>
                {ticket.pnrNumber && (
                  <div className={styles['pnr-number']}>PNR: {ticket.pnrNumber}</div>
                )}
              </div>

              <div className={styles['journey-details']}>
                {ticket.departureTime && (
                  <div className={styles['location-time']}>
                    <div className={styles['time']}>
                      {new Date(ticket.departureTime.seconds * 1000).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                    <div className={styles['date']}>
                      {new Date(ticket.departureTime.seconds * 1000).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                    <div className={styles['location']}>{ticket.origin}</div>
                  </div>
                )}

                <div className={styles['journey-duration']}>
                  <div className={styles['duration-line']}>
                    <span className={styles['dot']}></span>
                    <span className={styles['line']}></span>
                    <span className={styles['dot']}></span>
                  </div>
                  <div className={styles['duration-text']}>
                    {calculateDuration(ticket.departureTime, ticket.arrivalTime)}
                  </div>
                </div>

                {ticket.arrivalTime && (
                  <div className={styles['location-time']}>
                    <div className={styles['time']}>
                      {new Date(ticket.arrivalTime.seconds * 1000).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                    <div className={styles['date']}>
                      {new Date(ticket.arrivalTime.seconds * 1000).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                    <div className={styles['location']}>{ticket.destination}</div>
                  </div>
                )}
              </div>

              {ticket.notes && (
                <div className={styles['ticket-notes']}>
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <span>{ticket.notes}</span>
                </div>
              )}

              {/* Add visibility section */}
              <div className={styles['visibility-section']}>
                <span className={styles['visibility-label']}>Visible to:</span>
                {ticket.visibility && Array.isArray(ticket.visibility) && ticket.visibility.length > 0 ? (
                  <VisibilityUsers visibilityIds={ticket.visibility} />
                ) : (
                  <span className={styles['visibility-all']}>All event members</span>
                )}
              </div>

              <div className={styles['ticket-actions']}>
                {ticket.ticketPdfUrl && (
                  <button 
                    className={styles['action-button']}
                    onClick={() => {
                      setSelectedFile(ticket);
                      setIsFileModalOpen(true);
                    }}
                  >
                    View Ticket
                  </button>
                )}

                <div className={styles['action-icons']}>
                  <button 
                    className={styles['icon-button']}
                    onClick={() => handleEditTicket(ticket)}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button 
                    className={styles['icon-button']}
                    onClick={() => handleDeleteTicket(ticket)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ));
  };

  return (
    <div className={styles['main-layout']}>
    <TopBar />
    <LeftMenu />
    <div className={styles['ticket-main']}>
      <div className={styles['ticket-content']}>
      <AuthCheckWrapper>
          {/* Date Selector */}
          <div className={styles['date-selector']}>
            <button 
              className={styles['scroll-button']} 
              onClick={() => handleScroll('left')}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            
            <div className={styles['dates-container']} id="dateScroller">
              {selectedEvent?.eventDates?.map((date) => {
                const isRescheduled = date.rescheduleDate?.isRescheduled;
                const rescheduledDate = isRescheduled ? new Date(date.rescheduleDate.newDate) : null;
                const isCancelled = date.cancelled;
                const displayDate = isRescheduled ? rescheduledDate : new Date(date.date);

                return (
                  <button
                    key={date.id}
                    className={`${styles['date-button']} 
                      ${selectedDate === date.id ? styles['selected'] : ''}
                      ${isCancelled ? styles['cancelled'] : ''}
                      ${isRescheduled ? styles['rescheduled'] : ''}`}
                    onClick={() => handleDateSelect(date)}
                  >
                    <div className={styles['date-content']}>
                      <span className={styles['date-day']}>
                        {formatDate(displayDate)}
                      </span>
                      {date.event?.venueDetails?.venueData && (
                        <span className={styles['date-venue']}>
                          {date.event.venueDetails.venueData.displayName}
                        </span>
                      )}
                    </div>
                    <div className={styles['status-badges']}>
                      {isCancelled && (
                        <span className={styles['cancelled-badge']}>Cancelled</span>
                      )}
                      {isRescheduled && (
                        <span className={styles['rescheduled-badge']}>Rescheduled</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>            
            <button 
              className={styles['scroll-button']} 
              onClick={() => handleScroll('right')}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>

          {selectedDate && (
            <TicketModal 
              editingTicket={editingTicket}
              isOpen={isModalOpen}
              onClose={handleCloseModal}
            />
          )}

          <button 
            className={styles['add-ticket-button']}
            onClick={() => setIsModalOpen(true)}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Tickets
          </button>

          <div className={styles['tickets-container']}>
            {renderTicketCards()}
          </div>
          </AuthCheckWrapper>
        </div>
      </div>
      <RightMenu />
      {isFileModalOpen && selectedFile && (
        <FileViewerModal 
          isOpen={isFileModalOpen}
          onClose={() => {
            setIsFileModalOpen(false);
            setSelectedFile(null);
          }}
          file={{
            fileUrl: selectedFile.ticketPdfUrl,
            fileName: selectedFile.originalFileName || 'Ticket',
            fileType: 'application/pdf',
            size: 0 // Since we don't have the size information for existing tickets
          }}
          eventId={eventId}
          dateId={dateId}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <div className={styles['alert-modal-overlay']}>
          <div className={styles['alert-modal']}>
            {isDeleting && (
              <div className={styles['loading-overlay']}>
                <div className={styles['loading-spinner']} />
              </div>
            )}
            <div className={styles['alert-header']}>
              <FontAwesomeIcon 
                icon={faExclamationTriangle} 
                className={styles['alert-icon']} 
              />
              <h3 className={styles['alert-title']}>Delete Ticket</h3>
            </div>
            <p className={styles['alert-message']}>
              Are you sure you want to delete this ticket? This action cannot be undone.
            </p>
            <div className={styles['alert-actions']}>
              <button 
                className={`${styles['alert-button']} ${styles['cancel']}`}
                onClick={() => setDeleteConfirmation({
                  isOpen: false,
                  ticketId: null
                })}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className={`${styles['alert-button']} ${styles['delete']}`}
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tickets;